import { OptimizationType } from 'features/workload-optimization';

import { ScalingPolicyFormModel } from './_types';
import {
  minCpuConstraintValue,
  minMemoryConstraintValueInGib,
} from '../../_constants';
import { OptimizationThresholdType } from '../../_types';

const defaultVpaScalingPolicySettings: NonNullable<
  ScalingPolicyFormModel['vpa']
> = {
  overhead: { cpu: 0, memory: 10 },
  stallDetection: {
    cpuStallThresholdPercentage: 10,
    minPressuredPodPercentage: 50,
  },
  memoryGrowthDetection: {
    enabled: true,
  },
  recommendationPercentile: { cpu: 80, memory: 100 },
  autoscalerMode: 'IMMEDIATE',
  antiAffinity: 'IMMEDIATE',
  optimizationThreshold: {
    cpu: {
      type: OptimizationThresholdType.PERCENTAGE,
      percentage: 10,
    },
    memory: {
      type: OptimizationThresholdType.PERCENTAGE,
      percentage: 10,
    },
  },
  startupMetrics: {
    type: 'no-strategy' as const,
    min: 2,
    sec: 0,
    isCustom: false,
  },
  lookbackPeriod: {
    cpu: { days: 1, hours: 0, isCustom: false },
    memory: { days: 1, hours: 0, isCustom: false },
  },
  optimizedResources: { cpu: true, memory: true, gpu: false },
  excludedContainers: {
    enabled: false,
    containers: [],
  },
  workloadConstraints: {
    cpu: {
      mode: 'constant',
      min: minCpuConstraintValue,
      max: null,
    },
    memory: {
      mode: 'constant',
      min: minMemoryConstraintValueInGib,
      max: null,
    },
  },
  workloadResourceLimits: {
    cpu: {
      type: 'NO_LIMIT',
      multiplier: null,
      semiAutoMultiplier: null,
    },
    memory: {
      type: 'AUTO',
      multiplier: null,
      semiAutoMultiplier: null,
    },
  },
  downscalingMode: 'IMMEDIATE', // should be the same as autoscalerMode for backward compatibility
  memoryEventMode: 'IMMEDIATE',
  rolloutBehavior: {
    restartPodsOneByOne: false,
    zeroDowntime: false,
    delaySecondsEnabled: false,
    delaySeconds: { minutes: 1, seconds: 0, isCustom: false },
  },
  confidence: 90,
  customMetrics: { jvmMemoryOptimization: false, autoInstrument: false },
  hpaConverter: { enabled: false },
  predictiveScaling: { cpu: { enabled: false } },
};

const defaultHpaV2ScalingPolicySettings: NonNullable<
  ScalingPolicyFormModel['hpa']
> = {
  takeOwnership: { enabled: false },
  basicBehavior: {
    minReplicas: 3,
    maxReplicas: 20,
  },
  metrics: { cpu: 90, memory: null },
  autoscalerBehavior: {
    scaleUp: {
      stabilizationWindowSeconds: 0,
      pods: { value: 24, periodSeconds: 15 },
      percent: { value: 100, periodSeconds: 15 },
    },
    scaleDown: {
      stabilizationWindowSeconds: 300,
      pods: null,
      percent: { value: 100, periodSeconds: 15 },
    },
  },
};

export const defaultScalingPolicyFormModel: ScalingPolicyFormModel = {
  name: '',
  optimizationType: OptimizationType.OFF,
  vpa: defaultVpaScalingPolicySettings,
  hpa: defaultHpaV2ScalingPolicySettings,
};
